import { FormDateField } from "@/components/form/DateField";
import { FormInputField } from "@/components/form/InputField";
import { FormRadioGroupField } from "@/components/form/RadioGroupField";
import { SelectOption, FormSelectField } from "@/components/form/SelectField";
import { FormTextAreaField } from "@/components/form/TextAreaField";
import { Button } from "@/components/ui/button";
import { DialogContent, DialogHeader } from "@/components/ui/dialog";
import { useMutateUpdatePause } from "@/lib/hooks/query/pause/useMutateUpdatePause";
import {
  pauseUpsertSchema,
  PauseUpsertValues,
} from "@/lib/modals/pauses/pauseFormSchema";
import { useModal } from "@/lib/modals/useModal";
import {
  ParsedPause,
  PauseReasonType,
  PauseResponsible,
  pauseSolutionFilteredOptions,
  PauseSolutionType,
} from "@/lib/types/pause";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { useMemo } from "react";
import { Matcher } from "react-day-picker";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

export function EditPauseModal({ pause }: { pause: ParsedPause }) {
  const { t } = useTranslation(undefined, {
    keyPrefix: "common.modal.edit-pause",
  });
  const { t: tResponsible } = useTranslation(undefined, {
    keyPrefix: "common.pause-responsible",
  });
  const { t: tReason } = useTranslation(undefined, {
    keyPrefix: "common.pause-reason",
  });
  const { t: tSolution } = useTranslation(undefined, {
    keyPrefix: "common.pause-solution",
  });

  const { hideModal } = useModal();

  const { mutateAsync, isPending } = useMutateUpdatePause();

  const defaultValues = useMemo(() => {
    const solutionData =
      pause.status === "finished"
        ? {
            solvedAt: pause.solvedAt,
            solutionType: pause.solutionType,
            otherSolution: pause.otherSolution,
          }
        : {};
    return pauseUpsertSchema.parse({
      status: pause.status,
      startedAt: pause.startedAt,
      reasonType: pause.reasonType,
      responsible: pause.responsible,
      additionalInformation: pause.additionalInformation ?? "",
      otherReason: pause.otherReason,
      ...solutionData,
    });
  }, [pause]);

  const form = useForm<PauseUpsertValues>({
    resolver: zodResolver(pauseUpsertSchema),
    mode: "onBlur",
    defaultValues: defaultValues,
  });

  const handleSubmit = form.handleSubmit(async (values, event) => {
    event?.preventDefault();
    await mutateAsync({
      orderId: pause.orderId,
      pauseId: pause.id,
      pause: values,
    });
    hideModal();
  });

  const handleCancel = () => {
    hideModal();
  };

  const reasonOptions: SelectOption[] = Object.values(PauseReasonType).map(
    (reason) => ({
      value: reason,
      label: tReason(reason),
    }),
  );

  const selectedReason = form.watch("reasonType");
  const solutionOptions = useMemo(() => {
    if (!selectedReason) return [];
    return pauseSolutionFilteredOptions[selectedReason].map((solution) => ({
      value: solution,
      label: tSolution(solution),
    }));
  }, [selectedReason, tSolution]);

  const responsibleOptions: SelectOption[] = Object.values(
    PauseResponsible,
  ).map((responsible) => ({
    value: responsible,
    label: tResponsible(responsible),
  }));

  const statusValue = form.watch("status");
  const reasonValue = form.watch("reasonType");
  const solutionValue = form.watch("solutionType");

  const startedAtValue = form.watch("startedAt");
  const solvedAtValue = form.watch("solvedAt");

  const startedAtDisabled: Matcher = {
    after: solvedAtValue,
  };
  const solvedAtDisabled: Matcher = {
    before: startedAtValue,
  };

  return (
    <DialogContent className="top-5 flex max-h-[97dvh] translate-y-0 flex-col gap-6 sm:max-w-5xl">
      <DialogHeader className="text-2xl font-semibold text-[#1B261B]">
        {t("edit-process-timer-pause")}
      </DialogHeader>
      <FormProvider {...form}>
        <form
          id="new-pause-form"
          onSubmit={(e) => e.preventDefault()}
          className="h-full overflow-y-auto"
        >
          <div className="flex h-full flex-col gap-6 overflow-y-auto">
            <FormRadioGroupField
              key={statusValue}
              name="status"
              label={t("is-the-delay-ongoing-or-finished")}
              options={[
                { value: "ongoing", label: t("ongoing") },
                { value: "finished", label: t("finished") },
              ]}
            />
            <div className="flex flex-col gap-6">
              <FormDateField
                name="startedAt"
                label={t("pause-start")}
                placeholder={t("select-start-date")}
                disabledDates={startedAtDisabled}
              />
              <div className="flex flex-col gap-4">
                <FormSelectField
                  name="reasonType"
                  label={t("reason-for-delay")}
                  placeholder={t("select-reason")}
                  options={reasonOptions}
                  onChange={(value) => {
                    form.resetField("solutionType", {
                      defaultValue: undefined,
                    });
                    if (value === PauseReasonType.OTHER) {
                      form.setValue("reasonType", value);
                      form.setValue("otherReason", "");
                    } else {
                      form.setValue("reasonType", value as PauseReasonType);
                      form.setValue("otherReason", null);
                    }
                  }}
                />
                {reasonValue === PauseReasonType.OTHER && (
                  <FormInputField
                    name="otherReason"
                    placeholder={t("reason-for-delay")}
                    max={150}
                  />
                )}
              </div>
              <FormSelectField
                name="responsible"
                label={t("responsible-for-delay")}
                placeholder={t("select-responsible")}
                options={responsibleOptions}
              />
              <FormTextAreaField
                name="additionalInformation"
                label={t("additional-information")}
                placeholder={t("add-comment")}
                optional
              />
              {statusValue === "finished" && (
                <>
                  <FormDateField
                    name="solvedAt"
                    label={t("pause-end")}
                    placeholder={t("select-end-date")}
                    disabledDates={solvedAtDisabled}
                  />
                  <div className="flex flex-col gap-4">
                    <FormSelectField
                      key={selectedReason}
                      name="solutionType"
                      label={t("reason-for-solution")}
                      placeholder={t("select-solution")}
                      options={solutionOptions}
                      onChange={(value) => {
                        if (value === PauseSolutionType.OTHER) {
                          form.setValue("solutionType", value);
                          form.setValue("otherSolution", "");
                        } else {
                          form.setValue(
                            "solutionType",
                            value as PauseSolutionType,
                          );
                          form.setValue("otherSolution", null);
                        }
                      }}
                    />
                    {solutionValue === PauseSolutionType.OTHER && (
                      <FormInputField
                        name="otherSolution"
                        placeholder={t("reason-for-solution")}
                        max={150}
                      />
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </form>
        <div className="grid grid-cols-2 gap-2">
          <Button variant="secondary" onClick={handleCancel}>
            {t("cancel")}
          </Button>
          <Button
            form="new-pause-form"
            type="button"
            onClick={handleSubmit}
            disabled={isPending}
          >
            {isPending && <Loader2 className="mr-2 animate-spin" />}
            {t("save-edits")}
          </Button>
        </div>
      </FormProvider>
    </DialogContent>
  );
}
